import React, { useState, useEffect, Fragment, useRef } from 'react';
import {
    Modal, Box, Typography,
    Paper, IconButton, ListItem, Divider, Collapse, Button as MuiButton,
    Fade, Dialog, DialogTitle, DialogContent, DialogActions,
    CircularProgress, Chip, Grid, Stack
} from '@mui/material';
import Icon from '@mdi/react';
import { mdiCloseCircleOutline, mdiPencil, mdiChevronDown, mdiChevronRight, mdiChevronUp } from '@mdi/js';
import { useSelector, useDispatch } from 'react-redux';
import { liveTrackinglayoutSelector } from '../../../features/layout/layoutSlice';
import { selectedTripSelector, updateTripSettings, getOneTrip, updateRoutePathFromTrip } from '../../../features/tripcenter/tripControlSlice';
import { red, green } from '@mui/material/colors';
import { tripDurationFormat } from '../../../utils/dateFormat';
import RouteRulesModal from '../routeBuilderComponents/RouteRulesModal';
import RouteNotificationsModal from '../routeBuilderComponents/RouteNotificationsModal';
import RouteRevisionModal from './RouteRevisionModal';

const formatCategory = (category) => {
    switch (category) {
        case 'tripStage': return 'Trip Stage';
        case 'activeStatus': return 'Status Updates';
        case 'ruleViolation': return 'Rule Violations';
        case 'finalReport': return 'Final Report';
        default: return category;
    }
};



const TripSettingsModal = ({ open, handleClose }) => {
    const dimention = useSelector(liveTrackinglayoutSelector);
    const trip = useSelector(selectedTripSelector);
    const [rulesModalOpen, setRulesModalOpen] = useState(false);
    const [notificationsModalOpen, setNotificationsModalOpen] = useState(false);
    const [rulesExpanded, setRulesExpanded] = useState(false);
    const [notificationsExpanded, setNotificationsExpanded] = useState(false);
    const [revisionCollapsed, setRevisionCollapsed] = useState(false);
    const [revisionForm, setRevisionForm] = useState({ name: '', reason: '' });
    const [revisionErrors, setRevisionErrors] = useState({});
    const [revisionAttempted, setRevisionAttempted] = useState(false);
    const [revisionSubmitting, setRevisionSubmitting] = useState(false);
    const [revisionResponse, setRevisionResponse] = useState(null);
    const [responseDialogOpen, setResponseDialogOpen] = useState(false);
    const [revisionTripId, setRevisionTripId] = useState(null);
    const lastResponseRef = useRef(null);
    const dispatch = useDispatch();

    const isRouteRevision = trip?.tripStage === 'Completed';

    const validateRevisionForm = (values) => {
        const nextErrors = {};
        if (!values.name || !values.name.trim()) {
            nextErrors.name = 'Revised By is required.';
        }
        if (!values.reason || !values.reason.trim()) {
            nextErrors.reason = 'Reason is required.';
        }
        return nextErrors;
    };

    const handleRevisionFieldChange = (field, value) => {
        setRevisionForm((prev) => {
            const next = { ...prev, [field]: value };
            if (revisionAttempted) {
                setRevisionErrors(validateRevisionForm(next));
            }
            return next;
        });
    };

    const handleRevisionSubmit = async (event) => {
        if (event?.preventDefault) event.preventDefault();
        if (revisionSubmitting) return;
        setRevisionAttempted(true);
        const nextErrors = validateRevisionForm(revisionForm);
        setRevisionErrors(nextErrors);
        if (Object.keys(nextErrors).length > 0) return;
        if (!trip?._id) return;

        setRevisionSubmitting(true);
        setRevisionTripId(trip._id);
        const payload = {
            tripId: trip._id,
            name: revisionForm.name.trim(),
            reason: revisionForm.reason.trim(),
        };
        const result = await dispatch(updateRoutePathFromTrip(payload));
        setRevisionSubmitting(false);

        if (updateRoutePathFromTrip.fulfilled.match(result)) {
            setRevisionResponse(result.payload);
        } else {
            setRevisionResponse(result.payload || {
                success: false,
                message: result.error?.message || 'Route revision failed',
            });
        }
    };

    const handleResponseDialogClose = () => {
        setResponseDialogOpen(false);
        if (revisionResponse?.success) {
            setRevisionForm({ name: '', reason: '' });
            setRevisionAttempted(false);
            setRevisionErrors({});
        }
        setRevisionResponse(null);
        setRevisionTripId(null);
        lastResponseRef.current = null;
    };

    const handleEditClick = (section) => {
        if (section === 'rules') {
            setRulesModalOpen(true);
        } else if (section === 'notifications') {
            setNotificationsModalOpen(true);
        }
        //console.log(`Editing ${section}`);
    };

    const handleRulesModalClose = () => {
        setRulesModalOpen(false);
    };

    const handleRulesSubmit = async (updatedRules) => {
        try {
            //console.log('Updated rules:', updatedRules);
            dispatch(updateTripSettings({ tripId: trip._id, rules: updatedRules }));
            setTimeout(() => {
                dispatch(getOneTrip({ _id: trip._id }));
                handleRulesModalClose();
            }, 500);
        } catch (error) {
            console.error('Error updating rules:', error);
        }
    };

    const handleNotificationsModalClose = () => {
        setNotificationsModalOpen(false);
    };

    const handleNotificationsSubmit = async (updatedNotifications) => {
        try {
            //console.log('Updated notifications:', updatedNotifications);
            dispatch(updateTripSettings({
                tripId: trip._id,
                notifications: updatedNotifications
            }));
            setTimeout(() => {
                dispatch(getOneTrip({ _id: trip._id }));
                handleNotificationsModalClose();
            }, 500);
        } catch (error) {
            console.error('Error updating notifications:', error);
        }
    };

    const toggleRules = () => setRulesExpanded(!rulesExpanded);
    const toggleNotifications = () => setNotificationsExpanded(!notificationsExpanded);

    useEffect(() => {
        if (!open) {
            setRevisionCollapsed(false);
        }
    }, [open]);

    useEffect(() => {
        if (!revisionResponse || lastResponseRef.current === revisionResponse) return;
        lastResponseRef.current = revisionResponse;
        setResponseDialogOpen(true);
        setRevisionCollapsed(false);
        if (open) {
            handleClose();
        }
        if (revisionResponse.success && revisionTripId) {
            dispatch(getOneTrip({ _id: revisionTripId }));
        }
    }, [revisionResponse, open, handleClose, dispatch, revisionTripId]);

    if (!trip && !revisionResponse) return null;

    const renderNotificationRecipients = (recipients, type) => {
        const getContactInfo = (recipient) => {
            switch (type) {
                case 'email': return recipient.email;
                case 'push': return recipient.token;
                default: return recipient.number;
            }
        };

        return recipients.map((recipient, index) => (
            <Box
                key={index}
                sx={{
                    mt: 1,
                    p: 1,
                    bgcolor: 'background.paper',
                    borderRadius: 1,
                    border: '1px solid',
                    borderColor: 'divider'
                }}
            >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                    <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                        {recipient.name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        {getContactInfo(recipient)}
                    </Typography>
                </Box>
                <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>
                    <Box component="span" sx={{ fontWeight: 'bold' }}>Categories: </Box>
                    {recipient.categories.map((category, catIndex) => (
                        <React.Fragment key={catIndex}>
                            {formatCategory(category)}
                            {catIndex < recipient.categories.length - 1 ? ' , ' : ''}
                        </React.Fragment>
                    ))}
                </Typography>
            </Box>
        ));
    };

    const responseData = revisionResponse?.data || {};
    const responseDetails = revisionResponse?.details || {};
    const responseSegmentsCount = responseData?.segments?.length || 0;
    const responseArchivedCount = responseData?.archivedRoutes?.length || 0;
    const responseRouteDuration = responseData?.routeDuration;
    const responseRouteDurationLabel = responseRouteDuration !== undefined && responseRouteDuration !== null
        ? tripDurationFormat(responseRouteDuration, 's')
        : 'N/A';

    const getVisitChipColor = (visit) => {
        if (visit === 'Visited in Sequence') return 'success';
        if (visit === 'Visited out of Sequence') return 'warning';
        if (visit === 'Skipped') return 'error';
        return 'default';
    };

    return (
        <>
            {open && isRouteRevision && revisionCollapsed && (
                <Paper
                    sx={{
                        position: 'fixed',
                        top: dimention?.assetEventList?.top || 16,
                        left: dimention?.assetEventList?.left || 16,
                        zIndex: (theme) => theme.zIndex.modal + 1,
                        p: 1.5,
                        minWidth: 260,
                        maxWidth: 360,
                        borderRadius: 2,
                        boxShadow: 8,
                        bgcolor: 'background.paper'
                    }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
                        <Box>
                            <Typography variant="caption" color="text.secondary">
                                Route Revision
                            </Typography>
                            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                                {trip?.route?.routeName?.split('|')[0] || 'Unknown Route'}
                            </Typography>
                        </Box>
                        <IconButton onClick={() => setRevisionCollapsed(false)} size="small">
                            <Icon path={mdiChevronUp} size={0.9} />
                        </IconButton>
                    </Box>
                </Paper>
            )}

            {open && (!isRouteRevision || !revisionCollapsed) && (
                <Modal
                    open={open}
                    onClose={handleClose}
                    closeAfterTransition
                >
                    <Fade in={open}>
                        <Paper
                            sx={{
                                ...dimention.assetEventList,
                                bgcolor: 'background.paper',
                                maxHeight: '90vh',
                                minWidth: 360,
                                borderRadius: 2,
                                boxShadow: 24,
                                overflow: 'hidden',
                                display: 'flex',
                                flexDirection: 'column'
                            }}
                        >
                            <Box
                                sx={{
                                    p: 1.5,
                                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                    color: 'white',
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    gap: 1.5
                                }}
                            >
                                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                                    {isRouteRevision ? 'Route Revision' : 'Trip Settings'}
                                </Typography>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    {isRouteRevision && (
                                        <IconButton
                                            onClick={() => setRevisionCollapsed(true)}
                                            size="small"
                                            sx={{ color: 'white' }}
                                        >
                                            <Icon path={mdiChevronDown} size={0.9} color="white" />
                                        </IconButton>
                                    )}
                                    <IconButton onClick={handleClose} size="small" sx={{ color: 'white' }}>
                                        <Icon path={mdiCloseCircleOutline} size={0.95} color="white" />
                                    </IconButton>
                                </Box>
                            </Box>
                            <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 2, position: 'relative', overflow: 'auto', flex: 1 }}>
                                {(trip?.tripStage === 'Planned' || trip?.tripStage === 'Start Delayed' || trip?.tripStage === 'Active') && (
                                    <Fragment>
                                        <ListItem
                                            sx={{
                                                bgcolor: red[50],
                                                borderRadius: 1,
                                                mb: 1,
                                                flexDirection: 'column',
                                                alignItems: 'stretch',
                                            }}
                                        >
                                            <Box
                                                onClick={toggleRules}
                                                sx={{
                                                    display: 'flex',
                                                    justifyContent: 'space-between',
                                                    alignItems: 'center',
                                                    width: '100%',
                                                    cursor: 'pointer',
                                                    '&:hover': { opacity: 0.8 }
                                                }}
                                            >
                                                <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                                                    Trip Rules
                                                </Typography>
                                                <Icon
                                                    path={rulesExpanded ? mdiChevronDown : mdiChevronRight}
                                                    size={0.8}
                                                    color="primary"
                                                />
                                            </Box>
                                            <Collapse in={rulesExpanded} timeout="auto" unmountOnExit>
                                                <Box sx={{ mt: 1 }}>
                                                    {trip?.rules ? (
                                                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                                            {Object.entries(trip.rules).map(([key, value]) => (
                                                                <Box key={key} sx={{ display: 'flex', justifyContent: 'space-between', p: 0.5 }}>
                                                                    <Typography variant="body2" color="text.secondary">
                                                                        {key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
                                                                    </Typography>
                                                                    <Typography variant="body2">{value}</Typography>
                                                                </Box>
                                                            ))}
                                                        </Box>
                                                    ) : (
                                                        <Typography variant="body2" color="text.secondary">No rules configured</Typography>
                                                    )}
                                                    <MuiButton
                                                        variant="outlined"
                                                        size="small"
                                                        onClick={() => handleEditClick('rules')}
                                                        startIcon={<Icon path={mdiPencil} size={0.8} />}
                                                        sx={{ mt: 2 }}
                                                    >
                                                        Edit Rules
                                                    </MuiButton>
                                                </Box>
                                            </Collapse>
                                        </ListItem>

                                        <Divider />

                                        <ListItem
                                            sx={{
                                                bgcolor: green[50],
                                                borderRadius: 1,
                                                mb: 1,
                                                flexDirection: 'column',
                                                alignItems: 'stretch',
                                            }}
                                        >
                                            <Box
                                                onClick={toggleNotifications}
                                                sx={{
                                                    display: 'flex',
                                                    justifyContent: 'space-between',
                                                    alignItems: 'center',
                                                    width: '100%',
                                                    cursor: 'pointer',
                                                    '&:hover': { opacity: 0.8 }
                                                }}
                                            >
                                                <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                                                    Route Notifications
                                                </Typography>
                                                <Icon
                                                    path={notificationsExpanded ? mdiChevronDown : mdiChevronRight}
                                                    size={0.8}
                                                    color="primary"
                                                />
                                            </Box>
                                            <Collapse in={notificationsExpanded} timeout="auto" unmountOnExit>
                                                <Box sx={{ mt: 1 }}>
                                                    {trip?.notifications ? (
                                                        Object.entries(trip.notifications).map(([type, recipients]) => (
                                                            recipients.length > 0 && (
                                                                <Box key={type} sx={{ mt: 2 }}>
                                                                    <Typography
                                                                        variant="body2"
                                                                        sx={{
                                                                            fontWeight: 'bold',
                                                                            color: 'text.secondary',
                                                                            textTransform: 'uppercase',
                                                                            fontSize: '0.75rem',
                                                                            mb: 1
                                                                        }}
                                                                    >
                                                                        {type} Notifications
                                                                    </Typography>
                                                                    {renderNotificationRecipients(recipients, type)}
                                                                </Box>
                                                            )
                                                        ))
                                                    ) : (
                                                        <Typography variant="body2" color="text.secondary">
                                                            No notifications configured
                                                        </Typography>
                                                    )}
                                                    <MuiButton
                                                        variant="outlined"
                                                        size="small"
                                                        onClick={() => handleEditClick('notifications')}
                                                        startIcon={<Icon path={mdiPencil} size={0.8} />}
                                                        sx={{ mt: 2 }}
                                                    >
                                                        Edit Notifications
                                                    </MuiButton>
                                                </Box>
                                            </Collapse>
                                        </ListItem>

                                        <Divider />
                                    </Fragment>
                                )}

                                {isRouteRevision && (
                                    <RouteRevisionModal
                                        trip={trip}
                                        formValues={revisionForm}
                                        errors={revisionErrors}
                                        onFormChange={handleRevisionFieldChange}
                                        onSubmit={handleRevisionSubmit}
                                        submitting={revisionSubmitting}
                                    />
                                )}

                                {revisionSubmitting && (
                                    <Box
                                        sx={{
                                            position: 'absolute',
                                            inset: 0,
                                            bgcolor: 'rgba(255,255,255,0.7)',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            borderRadius: 2,
                                            zIndex: 5
                                        }}
                                    >
                                        <CircularProgress />
                                    </Box>
                                )}
                            </Box>
                        </Paper>
                    </Fade>
                </Modal>
            )}

            <Dialog
                open={responseDialogOpen}
                onClose={handleResponseDialogClose}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle
                    sx={{
                        bgcolor: revisionResponse?.success ? 'success.main' : 'error.main',
                        color: 'white',
                        fontWeight: 700
                    }}
                >
                    {revisionResponse?.success ? 'Route Revision Completed' : 'Route Revision Failed'}
                </DialogTitle>
                <DialogContent sx={{ pt: 3 }}>
                    {!revisionResponse?.success && (
                        <Box sx={{ mb: 2 }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 700, color: 'error.main', mb: 1 }}>
                                Route Revision failed. Try another trip on same route.
                            </Typography>
                            {revisionResponse?.message && (
                                <Typography variant="body2" color="text.secondary">
                                    {revisionResponse.message}
                                </Typography>
                            )}
                        </Box>
                    )}

                    {revisionResponse?.success && (
                        <Box sx={{ mb: 2 }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
                                Updated Route Summary
                            </Typography>
                            <Grid container spacing={2}>
                                <Grid item xs={12} sm={6}>
                                    <Typography variant="caption" color="text.secondary">Route Name</Typography>
                                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                        {responseData?.routeName || 'N/A'}
                                    </Typography>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <Typography variant="caption" color="text.secondary">Route Length</Typography>
                                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                        {responseData?.routeLength !== undefined && responseData?.routeLength !== null
                                            ? `${responseData.routeLength.toFixed(1)} km`
                                            : 'N/A'}
                                    </Typography>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <Typography variant="caption" color="text.secondary">Route Duration</Typography>
                                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                        {responseRouteDurationLabel}
                                    </Typography>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <Typography variant="caption" color="text.secondary">Segments</Typography>
                                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                        {responseSegmentsCount}
                                    </Typography>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <Typography variant="caption" color="text.secondary">Past Revisions</Typography>
                                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                        {responseArchivedCount}
                                    </Typography>
                                </Grid>
                            </Grid>
                        </Box>
                    )}

                    {revisionResponse?.errorCode === 'ROUTE_LOCATION_VALIDATION_FAILED' && (
                        <Box sx={{ mt: 2 }}>
                            <Divider sx={{ mb: 2 }} />
                            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
                                Location Validation Details
                            </Typography>

                            {responseDetails?.expectedSequence?.length > 0 && (
                                <Box sx={{ mb: 2 }}>
                                    <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                                        Expected Sequence
                                    </Typography>
                                    <Stack spacing={1}>
                                        {responseDetails.expectedSequence.map((item) => (
                                            <Paper
                                                key={`${item.expectedIndex}-${item.locationName}`}
                                                variant="outlined"
                                                sx={{ p: 1.5, borderRadius: 1.5 }}
                                            >
                                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1.5 }}>
                                                    <Box>
                                                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                                            {item.locationName}
                                                        </Typography>
                                                        <Typography variant="caption" color="text.secondary">
                                                            {item.type} • {item.locationType}
                                                        </Typography>
                                                    </Box>
                                                    <Chip
                                                        size="small"
                                                        label={item.visit}
                                                        color={getVisitChipColor(item.visit)}
                                                        sx={{ fontWeight: 600 }}
                                                    />
                                                </Box>
                                            </Paper>
                                        ))}
                                    </Stack>
                                </Box>
                            )}

                            {responseDetails?.skippedExpected?.length > 0 && (
                                <Box sx={{ mb: 2 }}>
                                    <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                                        Skipped Locations
                                    </Typography>
                                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                                        {responseDetails.skippedExpected.map((item) => (
                                            <Chip
                                                key={`${item.expectedIndex}-${item.locationName}`}
                                                label={item.locationName}
                                                color="error"
                                                variant="outlined"
                                                size="small"
                                            />
                                        ))}
                                    </Stack>
                                </Box>
                            )}
                        </Box>
                    )}
                </DialogContent>
                <DialogActions sx={{ px: 3, py: 2 }}>
                    <MuiButton onClick={handleResponseDialogClose} variant="contained">
                        Close
                    </MuiButton>
                </DialogActions>
            </Dialog>

            <RouteRulesModal
                open={rulesModalOpen}
                onClose={handleRulesModalClose}
                onSubmit={handleRulesSubmit}
                initialRules={trip?.rules}
                routeLength={trip?.route?.routeLength}
                routeDuration={trip?.route?.routeDuration}
            />

            <RouteNotificationsModal
                open={notificationsModalOpen}
                onClose={handleNotificationsModalClose}
                onSubmit={handleNotificationsSubmit}
                initialNotifications={trip?.notifications}
            />
        </>
    );
};

export default TripSettingsModal; 
